import React, { useState, useEffect } from 'react'
import { FormControl, FormLabel } from '@mui/material'

import {
  formatLocationData,
  isLocationData,
} from 'components/SignupFormQuestions/DemographicQuestion/Helper'
import { LocationSuggestion } from 'types/LocationData'
import {
  fetchLocationSuggestions,
  fetchLocationDetails,
} from 'api/SignUpFormQuestion/Action'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import { setAnswer } from 'redux/slices/SignupFormAnswers'
import LocationAutocomplete from 'components/SignupFormQuestions/DemographicQuestion/LocationAutocomplete'

/**
 * DemographicQuestion component renders a question with a location input field.
 *
 * Props:
 * @param {string} question - The question text to display.
 * @param {string} id - The unique identifier for the question.
 * @param {boolean} readOnlyMode - Flag to indicate if the component is in read-only mode.
 */
const DemographicQuestion = ({ question, id, readOnlyMode }: any) => {
  const { signupAnswers: { answers = [], initialAnswers = [] } = {} } =
    useAppSelector((state: any) => state) || {}

  const signupFormQuestions = useAppSelector(
    (state) => state.participantProfileSignupFormQuestions.data,
  )

  const targetQuestion = signupFormQuestions.find((q: any) => q.id === id)
  const subtype = targetQuestion ? targetQuestion.subtype : null
  const locationType = subtype === 'FullAddress' ? 'full_address' : 'postal'

  const dispatch = useAppDispatch()

  const initialLocation: LocationSuggestion = {
    label: answers[id]?.value?.text || answers[id]?.value?.addressFull || '',
    magicKey: '',
    value: answers[id]?.value?.id?.toString() || '',
  }

  const [selectedLocation, setSelectedLocation] =
    useState<LocationSuggestion>(initialLocation)
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([])

  const fetchSuggestions = async (query: string) => {
    try {
      const response = await fetchLocationSuggestions(query, locationType)
      const formattedSuggestions: LocationSuggestion[] = response.data.map(
        (item: any) => ({
          label: item.text,
          magicKey: item.magicKey,
          value: item.id.toString(),
        }),
      )
      setSuggestions(formattedSuggestions)
    } catch (error) {
      console.error('Error fetching location suggestions:', error)
    }
  }

  useEffect(() => {
    if (!readOnlyMode) {
      fetchSuggestions('')
    }
    if (!readOnlyMode && !initialAnswers[id]?.value) {
      dispatch(setAnswer({ questionId: id, answer: { value: {} } }))
    }
    if (readOnlyMode) {
      // Dispatch empty object if the initial answer is null or empty
      const answerVal = answers[id]?.value
      const initialLocation: LocationSuggestion = {
        label: answerVal?.text || answerVal?.addressFull || '',
        magicKey: '',
        value: answerVal?.id?.toString() || '',
      }
      setSelectedLocation(initialLocation)
    }
  }, [readOnlyMode])

  useEffect(() => {
    if (!readOnlyMode && selectedLocation.label.length > 2) {
      fetchSuggestions(selectedLocation.label)
    } else if (readOnlyMode) {
      setSuggestions([])
    }
  }, [selectedLocation, readOnlyMode])

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    newInputValue: string,
  ) => {
    setSelectedLocation({
      label: newInputValue,
      value: selectedLocation.value,
      magicKey: selectedLocation.magicKey,
    })
  }

  const handleSelectionChange = async (
    event: React.SyntheticEvent<Element, Event>,
    value: string | LocationSuggestion | null,
  ) => {
    if (typeof value === 'object' && value !== null) {
      setSelectedLocation(value)

      try {
        const response = await fetchLocationDetails(
          value.label,
          value.magicKey,
          locationType,
        )
        const formattedData = formatLocationData(response)
        if (formattedData && isLocationData(formattedData)) {
          dispatch(
            setAnswer({ questionId: id, answer: { value: formattedData } }),
          )
        } else {
          console.error(
            'Formatted data is null or in an incorrect format:',
            formattedData,
          )
        }
      } catch (error) {
        console.error('Error fetching location details:', error)
      }
    } else {
      dispatch(setAnswer({ questionId: id, answer: { value: {} } }))
    }
  }

  return (
    <FormControl fullWidth>
      <FormLabel>{question}</FormLabel>
      <LocationAutocomplete
        suggestions={suggestions}
        selectedLocation={selectedLocation}
        onInputChange={handleInputChange}
        onSelectionChange={handleSelectionChange}
        readOnlyMode={readOnlyMode}
      />
    </FormControl>
  )
}

export default DemographicQuestion
